import { useCallback, useState } from "react";
import { QrScan } from "./scan/QrScan.js";
import { feedback } from "./scan/feedback.js";
import type { CodeState } from "./scan/camera.js";
import { adoptSession, applyPrefs, applySetupPayload, Auth, getDevice, getPrefs, getSession, getSettings, saveDevice, savePrefs, saveSession, saveSettings, settingsReady, type Prefs } from "./api/client.js";
import { getDiagKey, setDiagKey } from "./diag/client.js";
import { healthSnapshot } from "./diag/health.js";
import { getLang, setLang, t, type Lang } from "./i18n.js";
import { Actions, Banner, Btn, Strip } from "./ui/components.jsx";

/** Device settings: enrolment (the administrator's code, scanned), the shift in progress,
 *  theme, mounted mode, language; the development connection settings behind a fold. */

/** Viewport numbers for the blank-band investigation (installed iOS app, 2026-10-28). */
function layoutProbe(): string {
  const nav = navigator as Navigator & { standalone?: boolean };
  const probe = document.createElement("div");
  probe.style.cssText = "position:fixed;top:env(safe-area-inset-top);bottom:env(safe-area-inset-bottom);visibility:hidden;pointer-events:none";
  document.body.appendChild(probe);
  const r = probe.getBoundingClientRect();
  probe.remove();
  const el = (sel: string) => Math.round(document.querySelector(sel)?.getBoundingClientRect().height ?? 0);
  const vh = document.createElement("div");
  vh.style.cssText = "position:absolute;top:0;height:100vh;visibility:hidden;pointer-events:none";
  document.body.appendChild(vh);
  const vhPx = Math.round(vh.getBoundingClientRect().height);
  vh.remove();
  return [
    `inner ${window.innerWidth}×${window.innerHeight}`,
    `vv ${Math.round(window.visualViewport?.height ?? 0)}`,
    `screen ${screen.width}×${screen.height}`,
    `standalone ${String(nav.standalone)}`,
    `vh ${vhPx} client ${document.documentElement.clientHeight}`,
    `safe ${Math.round(r.top)}/${Math.round(window.innerHeight - r.bottom)}`,
    `body ${el("body")} root ${el("#root")} screen ${el(".screen")}`,
  ].join(" · ");
}

export function Settings({ onClose, onScanTest }: { onClose: () => void; onScanTest: () => void }) {
  const [s, setS] = useState(getSettings);
  const [p, setP] = useState<Prefs>(getPrefs);
  const [userTheme, setUserTheme] = useState<"dark" | "light" | "auto" | "">(() => getSession()?.theme ?? "");
  const [enrolCode, setEnrolCode] = useState("");
  const [lang, setLangState] = useState<Lang>(getLang);
  const [diag, setDiag] = useState(getDiagKey);
  const [camera, setCamera] = useState(false);
  const [note, setNote] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);
  const [advanced, setAdvanced] = useState(() => !getDevice() && settingsReady());
  const device = getDevice();
  const session = getSession();

  /** The camera reads either an enrolment link (/?enrol=…) or the legacy setup link. */
  const onCode = useCallback(async (data: string): Promise<CodeState> => {
    const text = data.replace(/^\]\w\w/, "").trim();
    let code: string | null = null;
    try {
      code = new URL(text, location.origin).searchParams.get("enrol");
    } catch {
      code = null;
    }
    if (code) {
      try {
        const d = await Auth.enrol(code);
        saveDevice(d);
        feedback("accepted");
        setNote({ kind: "ok", text: t("enrol.done", { name: d.name }) });
        setCamera(false);
        return "accepted";
      } catch {
        feedback("rejected");
        setNote({ kind: "bad", text: t("enrol.failed") });
        return "rejected";
      }
    }
    const r = applySetupPayload(data);
    if (!r) {
      feedback("rejected");
      return "rejected";
    }
    feedback("accepted");
    setS({ ...getSettings() });
    setNote({ kind: "ok", text: t("settings.setupDone") });
    setCamera(false);
    return "accepted";
  }, []);
  if (camera) return <QrScan title={t("enrol.qrHint")} onCode={(text) => onCode(text).then((r) => { if (r !== "accepted") setCamera(false); })} onClose={() => setCamera(false)} />;

  const save = async () => {
    saveSettings(s);
    savePrefs(p);
    setLang(lang);
    setDiagKey(diag);
    const sess = getSession();
    if (sess) {
      // Logged in: theme and language are the user's own and travel with the account.
      try {
        const r = await Auth.updateMe({ theme: userTheme || null, language: lang });
        saveSession({ ...sess, theme: r.theme, language: r.language });
      } catch {
        /* offline: the local choice still applies on this device */
      }
    }
    applyPrefs(p);
    onClose();
  };
  const switchTo = async (tenantId: string) => {
    if (!session || tenantId === session.tenantId) return;
    try {
      const s = await Auth.switchTenant(tenantId);
      await adoptSession(s.token, s.expiresAt);
      window.location.reload();
    } catch {
      setNote({ kind: "bad", text: t("login.failed") });
    }
  };
  const endShift = async () => {
    try {
      await Auth.logout();
    } catch {
      /* the session is dropped locally regardless */
    }
    saveSession(null);
    onClose();
  };

  return (
    <div className="screen">
      <Strip left={<b>{t("settings.title")}</b>} online={navigator.onLine} queued={0} stuck={0} />
      <div className="body">
        {note && <Banner kind={note.kind} label={note.kind === "ok" ? t("banner.accepted") : t("banner.rejected")} message={note.text} />}
        <h2 className="sec">{t("enrol.title")}</h2>
        <p className="hint">{device ? t("enrol.device", { name: device.name, role: device.role }) : t("enrol.none")}</p>
        <Btn kind={device ? "secondary" : "primary"} icon="camera" onClick={() => setCamera(true)}>{t("enrol.scan")}</Btn>
        <label className="field"><span>{t("enrol.code")}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <input className="input" value={enrolCode} onChange={(e) => setEnrolCode(e.target.value.trim())} autoComplete="off" autoCapitalize="characters" spellCheck={false} placeholder={t("enrol.codePlaceholder")} />
            <Btn kind="secondary" grow={false} disabled={!enrolCode} onClick={() => void onCode(`${location.origin}/?enrol=${encodeURIComponent(enrolCode)}`).then((r) => { if (r === "accepted") setEnrolCode(""); })}>{t("enrol.enter")}</Btn>
          </div>
        </label>
        {session && (
          <>
            <p className="hint">{t("shift.loggedIn", { name: session.name })}</p>
            {(session.tenants?.length ?? 0) > 1 && (
              <label className="field"><span>{t("login.tenant")}</span>
                <select className="select" value={session.tenantId} onChange={(e) => void switchTo(e.target.value)}>
                  {session.tenants!.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
            )}
            <Btn kind="danger" onClick={() => void endShift()}>{t("shift.end")}</Btn>
          </>
        )}
        <h2 className="sec">{session ? t("settings.mine") : t("settings.title")}</h2>
        {session ? (
          <label className="field"><span>{t("settings.theme")} <small>{t("settings.onAccount")}</small></span>
            <select className="select" value={userTheme} onChange={(e) => setUserTheme(e.target.value as "dark" | "light" | "auto" | "")}>
              <option value="">{t("settings.theme.device")}</option>
              <option value="dark">{t("settings.theme.dark")}</option>
              <option value="light">{t("settings.theme.light")}</option>
              <option value="auto">{t("settings.theme.auto")}</option>
            </select>
          </label>
        ) : (
          <label className="field"><span>{t("settings.theme")}</span>
            <select className="select" value={p.theme} onChange={(e) => setP({ ...p, theme: e.target.value as Prefs["theme"] })}>
              <option value="dark">{t("settings.theme.dark")}</option>
              <option value="light">{t("settings.theme.light")}</option>
              <option value="auto">{t("settings.theme.auto")}</option>
            </select>
          </label>
        )}
        <label className="toggle"><input type="checkbox" checked={p.mounted} onChange={(e) => setP({ ...p, mounted: e.target.checked })} />{t("settings.mounted")}</label>
        <label className="toggle"><input type="checkbox" checked={p.keyboard} onChange={(e) => setP({ ...p, keyboard: e.target.checked })} />{t("settings.keyboard")}</label>
        <label className="field"><span>{t("settings.sound")} <small>{t("settings.sound.hint")}</small></span>
          <select className="input" value={p.sound ?? "loud"} onChange={(e) => { setP({ ...p, sound: e.target.value as "off" | "normal" | "loud" }); }}>
            <option value="off">{t("settings.sound.off")}</option>
            <option value="normal">{t("settings.sound.normal")}</option>
            <option value="loud">{t("settings.sound.loud")}</option>
          </select>
        </label>
        <label className="field"><span>{t("settings.gsSubs")} <small>{t("settings.gsSubs.hint")}</small></span><input className="input" value={p.gsSubstitutes} onChange={(e) => setP({ ...p, gsSubstitutes: e.target.value.replace(/\s/g, "").slice(0, 4) })} autoComplete="off" autoCapitalize="off" spellCheck={false} placeholder="~|#" /></label>
        <label className="field"><span>{t("settings.lang")}</span>
          <select className="select" value={lang} onChange={(e) => setLangState(e.target.value as Lang)}>
            <option value="sv">Svenska</option>
            <option value="en">English</option>
          </select>
        </label>
        <Btn kind="secondary" icon="test" onClick={onScanTest}>{t("settings.scantest")}</Btn>
        <button type="button" className="chip" onClick={() => setAdvanced((a) => !a)}>{t("settings.advanced")}</button>
        {advanced && (
          <>
            <label className="field"><span>{t("settings.token")}</span><input className="input" value={s.apiToken} onChange={(e) => setS({ ...s, apiToken: e.target.value })} autoComplete="off" autoCapitalize="off" spellCheck={false} /></label>
            <label className="field"><span>{t("settings.tenant")}</span><input className="input" value={s.tenantId} onChange={(e) => setS({ ...s, tenantId: e.target.value })} autoComplete="off" autoCapitalize="off" spellCheck={false} placeholder="UUID" /></label>
            <label className="field"><span>{t("settings.actor")}</span><input className="input" value={s.actorId} onChange={(e) => setS({ ...s, actorId: e.target.value })} autoComplete="off" /></label>
            <label className="field"><span>{t("settings.diag")}</span><input className="input" value={diag} onChange={(e) => setDiag(e.target.value)} autoComplete="off" autoCapitalize="off" /></label>
            <p className="hint">{(() => { const h = healthSnapshot(); return t("settings.health", { min: h.uptimeMinutes, n: h.stallsLastHour, worst: h.worstSeconds, mem: h.memoryMb === null ? "–" : `${h.memoryMb} MB` }); })()}</p>
            <p className="hint">{t("settings.device", { id: s.deviceId })}</p>
            <p className="hint" style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{t("settings.layout", { layout: layoutProbe() })}</p>
          </>
        )}
        <p className="hint">Build {__BUILD_VERSION__} · {new Date(__BUILD_TIME__).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}</p>
      </div>
      <Actions>
        <Btn kind="quiet" icon="back" onClick={onClose} />
        <Btn kind="primary" onClick={() => void save()}>{t("settings.save")}</Btn>
      </Actions>
    </div>
  );
}
